import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { useClientTable } from "../../../components/table/tableFeatures";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { commaSearchFilterFn, customFilterFn } from "../../../components/table/filterFns";
import { idsToVisibilityState, resetTable } from "../../../components/table/utils";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { CreativeExpander } from "./CreativeExpander";

const columnHelper = createColumnBuilder();

const creativeSearchFilterFn = commaSearchFilterFn((row) => row.creative_name ?? "");

const defaultSelectedColumns = [
  "creative_name",
  "creative_type",
  "dsp_sales",
  "dsp_total_sales",
  "dsp_ecpm",
  "dsp_impressions",
  "dsp_clicks",
  "dsp_cost",
  "dsp_total_purchases",
  "dsp_units",
  "dsp_ctr",
];

export const DSPCreativesTable = ({ data, isLoading, expanderFilters }) => {
  const { t } = useTranslation(["dsp", "common"]);
  const standardMetricIds = useStandardEntityMetrics("dspCreative");

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.creative_name ?? undefined, {
        id: "creative_name",
        header: t(($) => $.dsp["Name"]),
        size: 300,
        minSize: 200,
        maxSize: 500,
        enableHiding: false,
        enableColumnFilter: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
      }),
      columnHelper.accessor((row) => row.creative_type ?? undefined, {
        id: "creative_type",
        header: t(($) => $.dsp["Type"]),
        size: 180,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        meta: {
          columnSelectorOptions: { description: t(($) => $.dsp["The creative's type"]) },
        },
      }),
      columnHelper.accessor((row) => row.creative_size ?? undefined, {
        id: "creative_size",
        header: t(($) => $.dsp["Size"]),
        size: 130,
        enableResizing: false,
        sortFn: "alphanumeric",
        sortUndefined: "last",
        meta: {
          columnSelectorOptions: { description: t(($) => $.dsp["The creative's size"]) },
        },
      }),
      ...standardMetricIds.map((id) => columnHelper.metric(id)),
    ],
    [standardMetricIds, t],
  );

  const defaultColumnVisibility = useMemo(
    () =>
      idsToVisibilityState(
        defaultSelectedColumns,
        columns.filter((column) => Boolean(column.id)).map((column) => column.id),
      ),
    [columns],
  );

  const { state, initialState, setters } = usePersistedTableState({
    persistKey: "dspCreativesTable",
    initialState: {
      sorting: [{ id: "dsp_sales", desc: true }],
      columnVisibility: defaultColumnVisibility,
      // Keeps the name first when the column selector reorders the other columns.
      columnPinning: { start: ["creative_name"], end: [] },
    },
  });

  const table = useClientTable({
    data,
    columns,
    getRowId: (row) => String(row.creative_id),
    getRowCanExpand: () => true,
    defaultColumn: { filterFn: customFilterFn },
    globalFilterFn: creativeSearchFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              aria-label={t(($) => $.common.table["Reset table filters"])}
              onPress={() => resetTable(table)}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions["Search"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter ?? ""}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} renderExpander={(row) => <CreativeExpander row={row} expanderFilters={expanderFilters} />} />
    </>
  );
};
